import { GetStaticProps, NextPage } from "next";

import LiveQuery from "@sanity/preview-kit/live-query";
import { QueryParams } from "next-sanity";
import HeroTwoColumn from "@/components/PageBuilder/heroTwoColumn";
import AboutUsPressSection from "@/components/aboutUsPressSection";
import Block from "@/components/block";
import GivingBackSection from "@/components/givingBackSection";
import OurTeamSection from "@/components/ourTeamSection";
import { AboutUsData, getAboutUsQuery } from "@/queries/aboutUsQuery";
import { PageProps } from "@/types/pageProps";
import { Previewable } from "@/types/previewable";
import { getStaticPageData } from "@/utils/getStaticPageData";
import { Colorways } from "src/theme";
import HeaderNavigation from "@/components/HeaderNavigation/HeaderNavigation";
import {
  HeaderNavigationProps,
  HeaderNavigationQuery,
} from "@/components/HeaderNavigation";
import CompanyOverview from "@/components/companyOverview";
import { getClient } from "lib/sanity.server";
import { isEmpty } from "@/utils/isEmpty";
import SEOPageHead from "@/components/SEOPageHead";
import FooterModular from "@/components/FooterModular";
import {
  FooterNavigationProps,
  FooterNavigationQuery,
} from "@/components/FooterNavigation/query";

export type AboutUsProps = Previewable<PageProps<AboutUsData>>;

const token = process.env.SANITY_API_TOKEN;

const AboutUs: NextPage<AboutUsProps> = (props: AboutUsProps) => {
  const aboutUsData = props.payload.data;

  if (isEmpty(aboutUsData)) {
    return null;
  }

  const {
    givingBack,
    donatedValue,
    press,
    ourTeam,
    hero,
    companyOverview,
    navSections,
    footerPayload,
    seo,
  }: AboutUsData &
    HeaderNavigationProps & { footerPayload: FooterNavigationProps } =
    aboutUsData;

  return (
    <>
      <LiveQuery
        enabled={props.preview}
        query={getAboutUsQuery(props.preview)}
        initialData={aboutUsData}
      >
        <>
          {seo && <SEOPageHead seo={seo} />}
          <HeaderNavigation navSections={navSections} />
          <div className="mt-20">
            {hero && (
              <HeroTwoColumn
                colorway={Colorways.Default}
                {...hero}
                key={"hero"}
              />
            )}
          </div>
          <Block flushTop={true} className="relative">
            <section id="company-overview" className="">
              <CompanyOverview companyOverview={companyOverview} />
            </section>

            <section id="team" className="mt-[130px]">
              <OurTeamSection ourTeam={ourTeam} />
            </section>

            <section id="giving-back" className="mt-[100px]">
              <GivingBackSection
                givingBack={givingBack}
                totalDonated={donatedValue!}
              />
            </section>

            <section
              id="press"
              className="mt-[60px] border-t-4 border-platinum pt-20"
            >
              <AboutUsPressSection press={press} />
            </section>
          </Block>
          <FooterModular footerData={props.payload.data.footerPayload} />
        </>
      </LiveQuery>
    </>
  );
};

export default AboutUs;

export const getStaticProps: GetStaticProps<AboutUsProps> = async (context) => {
  const preview: boolean = !!context.draftMode;
  const queryParams: QueryParams = {};
  const query = getAboutUsQuery(preview);
  let aboutUsPayload = await getStaticPageData<AboutUsData>({
    query,
    queryParams,
    preview,
  });

  const navPayload: HeaderNavigationProps = await getClient(preview).fetch(
    HeaderNavigationQuery.query,
    {},
  );

  const footerData: FooterNavigationProps = await getClient(preview).fetch(
    FooterNavigationQuery.query,
    {},
  );

  if (
    aboutUsPayload === null ||
    navPayload === null ||
    isEmpty(navPayload) ||
    !aboutUsPayload.data.pageFeatureFlag?.enabled
  ) {
    return {
      notFound: true,
    };
  }

  aboutUsPayload = {
    ...aboutUsPayload,
    data: {
      ...aboutUsPayload.data,
      ...navPayload,
      footerPayload: footerData,
    },
  };

  return {
    props: {
      payload: {
        ...aboutUsPayload,
      },
      preview,
      token: preview ? token : "",
    },
  };
};
